import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Box, Button, Container, Fade, Grid, Typography, useMediaQuery } from '@mui/material'
import { ArrowForward, Favorite, Star } from '@mui/icons-material'

const Slideshow = React.memo(function Slideshow ({ allImages }) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [prevIndex, setPrevIndex] = useState(null)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [imagesLoaded, setImagesLoaded] = useState(new Set())

  const intervalRef = useRef(null)
  const transitionTimeoutRef = useRef(null)

  const preloadImage = useCallback((index) => {
    const image = allImages[index]
    if (!image || imagesLoaded.has(image)) return

    const img = new Image()
    img.src = `/web/${image}`
    img.onload = () => {
      setImagesLoaded(prev => new Set(prev).add(image))
    };
  }, [allImages, imagesLoaded]);

  useEffect(() => {
    if (allImages.length > 1) {
      preloadImage(1)
      preloadImage(2)
    }
  }, [allImages.length, preloadImage]);

  useEffect(() => {
    if (allImages.length <= 1) return

    const startTransition = () => {
      setPrevIndex(activeIndex)
      const next = (activeIndex + 1) % allImages.length
      preloadImage(next)
      setActiveIndex(next)
      setIsTransitioning(true)

      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current)
      }

      transitionTimeoutRef.current = setTimeout(() => {
        setIsTransitioning(false)
        setPrevIndex(null)
      }, 1200);
    };

    intervalRef.current = setInterval(startTransition, 6000)

    return () => {
      clearInterval(intervalRef.current)
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current)
      }
    };
  }, [allImages.length, activeIndex, preloadImage]);

  const currentImage = allImages[activeIndex]
  const previousImage = prevIndex != null ? allImages[prevIndex] : null
  console.log(previousImage)
  console.log(currentImage)
  return (<>
     {previousImage && (<Box
       key={previousImage}
       component="img"
       src={`/web/${previousImage}`}
       alt="Previous slide"
       loading="lazy"
       sx={{
         position:   'absolute',
         inset:      0,
         width:      '100%',
         height:     '100%',
         objectFit:  'cover',
         opacity:    isTransitioning ? 0.5 : 1,
         zIndex:     0,
         transition: {
           xs: 'opacity 1s ease', md: 'opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1)'
         },
         filter:     {
           xs: 'brightness(0.8)', md: 'brightness(0.9)'
         }
       }}
      />)}

     {currentImage && (<Box
       key={currentImage}
       component="img"
       src={`/web/${currentImage}`}
       alt="Current slide"
       loading="lazy"
       sx={{
         position:   'absolute',
         inset:      0,
         width:      '100%',
         height:     '100%',
         objectFit:  'cover',
         opacity:    isTransitioning ? 1 : 0.5,
         zIndex:     1,
         transition: {
           xs: 'opacity 1s ease', md: 'opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1)'
         },
         filter:     {
           xs: 'brightness(0.8)', md: 'brightness(0.9)'
         }
       }}
      />)}
   </>);
});

const useScrollProgress = () => {
    const [scrollProgress, setScrollProgress] = useState(0)

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop
            const docHeight = document.documentElement.scrollHeight - window.innerHeight
            const progress = Math.min((scrollTop / docHeight) * 100, 100)
            setScrollProgress(progress)
        }

        window.addEventListener('scroll', handleScroll, {passive: true})
        handleScroll()

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    return scrollProgress
}

const HomePage = () => {
    const [backgroundImageIndex, setBackgroundImageIndex] = useState(0)
    const [isVisible, setIsVisible] = useState(false)
    const [nextImageIndex, setNextImageIndex] = useState(1)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [mousePosition, setMousePosition] = useState({x: 0, y: 0})
    const scrollProgress = useScrollProgress()
    const isMobile = useMediaQuery('(max-width:768px)')
    const heroRef = useRef(null)
    const ctaRef = useRef(null)
    const [imagesLoaded, setImagesLoaded] = useState(new Set())
    const intervalRef = useRef(null)
    const transitionTimeoutRef = useRef(null)

    const scrollToCTA = () => {
        if (ctaRef.current) {
            const yOffset = -70;
            const element = ctaRef.current;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

            window.scrollTo({
                top: y,
                behavior: 'smooth'
            });
        }
    }

    useEffect(() => {
      setIsVisible(true)
    }, [])

  useEffect(() => {
        const handleMouseMove = (e) => {
            if (isMobile) return
            setMousePosition({
                x: (e.clientX / window.innerWidth) * 100,
                y: (e.clientY / window.innerHeight) * 100
            })
        }
        window.addEventListener('mousemove', handleMouseMove, {passive: true})
        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
        }
    }, [isMobile])

  const allImages = useMemo(() => {
    const modules = import.meta.glob('/public/web/*.JPG', { eager: true })

    const imgs = Object.keys(modules).map(path => {
      return path.split('/').pop()

    });

    console.log(modules)

    for (let i = imgs.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [imgs[i], imgs[j]] = [imgs[j], imgs[i]]
    }

    console.log(imgs)
    return imgs
  }, []);


    const FloatingElement = ({delay, children, style}) => (
        <Box
            sx={{
                animation: `float 6s ease-in-out ${delay}s infinite`,
                '@keyframes float': {
                    '0%, 100%': {transform: 'translateY(0px) rotate(0deg)'},
                    '50%': {transform: 'translateY(-20px) rotate(5deg)'}
                },
                ...style
            }}
        >
            {children}
        </Box>
    )


    return (<Box sx={{ position: 'relative', overflow: 'hidden', background: '#FAF9F7' }}>
       <Box
        sx={{
          position:      'absolute',
          inset:         0,
          pointerEvents: 'none',
          opacity:       { xs: 0.02, md: 0.03 },
          background:    `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, #B89968 0%, transparent 50%)`,
          zIndex:        0
        }}
       />
       <Box
        sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 3, zIndex: 1200, pointerEvents: 'none' }}>
         <Box
          sx={{
            height:     '100%',
            width:      `${scrollProgress}%`,
            background: 'linear-gradient(90deg, #675944 0%, #8B7355 100%)',
            boxShadow:  '0 0 10px rgba(103, 89, 68, 0.5)',
            transition: 'width 0.2s ease-out'
          }}
         />
       </Box>
       <Box sx={{
         position:      'absolute',
         top:           '18%',
         left:          '4%',
         opacity:       { xs: 0.02, md: 0.05 },
         pointerEvents: 'none',
         zIndex:        1,
         display:       { xs: 'none', md: 'block' }
       }}>
         <Star sx={{ fontSize: 60, color: '#B89968' }}/>
       </Box>
       <Box sx={{
         position:      'absolute',
         top:           '70%',
         right:         '8%',
         opacity:       { xs: 0.02, md: 0.05 },
         pointerEvents: 'none',
         zIndex:        1,
         display:       { xs: 'none', md: 'block' }
       }}>
         <Favorite sx={{ fontSize: 40, color: '#B89968' }}/>
       </Box>

       <FloatingElement delay={0} style={{ position: 'fixed', top: '20%', left: '5%', opacity: 0.05 }}>
         <Star sx={{ fontSize: 60, color: '#B89968' }}/>
       </FloatingElement>
       <FloatingElement delay={2} style={{ position: 'fixed', top: '70%', right: '10%', opacity: 0.05 }}>
         <Favorite sx={{ fontSize: 40, color: '#B89968' }}/>
       </FloatingElement>

       <Box
        ref={heroRef}
        sx={{
          minHeight:  { xs: '100dvh', md: '100vh' },
          display:    'flex',
          alignItems: 'center',
          position:   'relative',
          overflow:   'hidden',
          zIndex:     2
        }}
       >

         <Slideshow allImages={allImages}/>

         <Box
          sx={{
            position:      'absolute',
            inset:         0,
            background:    'linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.25))',
            zIndex:        2,
            pointerEvents: 'none'
          }}
         />

         <Box
          sx={{
            position:      'absolute',
            inset:         0,
            background:    'linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.25))',
            zIndex:        3,
            pointerEvents: 'none'
          }}
         />



         <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, px: { xs: 2, sm: 3 } }}>
           <Grid container spacing={6} alignItems="center" justifyContent="center">
             <Grid item xs={12} md={8} lg={6}>
               <Fade in={isVisible} timeout={1000}>
                 <Box sx={{
                   textAlign:  'center',
                   color:      'white',
                   textShadow: { xs: '0 1px 4px rgba(0,0,0,0.25)', md: '0 2px 8px rgba(0,0,0,0.3)' }
                 }}>
                   <Box
                    component="img"
                    src="/images/ImseenNBGLogo.svg"
                    alt="Imseen Interiors Logo"
                    loading="eager"
                    sx={{
                      width:    { xs: '72%', sm: '60%', md: '50%', lg: '42%' },
                      maxWidth: 580,
                      height:   'auto',
                      mx:       'auto',
                      display:  'block',
                      filter:   'brightness(0) invert(1) drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
                      opacity:  0.95,
                      mt:       { xs: '-40px', sm: '-60px', md: '-80px' },
                      mb:       -2
                    }}
                   />
                   <Typography variant="h2" sx={{
                     fontWeight: 300,
                     mb:         1,
                     fontSize:   { xs: '2.0rem', md: '3.3rem', lg: '3.75rem' },
                     fontFamily: '"Cormorant Garamond", serif',
                     lineHeight: 1.1,
                     fontStyle:  'italic'
                   }}>
                     <Box component="span" sx={{
                       background:           'linear-gradient(135deg, #FFFFFF 0%, #F5F3F0 100%)',
                       backgroundClip:       'text',
                       WebkitBackgroundClip: 'text',
                       color:                'transparent',
                       display:              'block'
                     }}>
                       Where Your Vision
                     </Box>
                   </Typography>
                   <Typography variant="h2" sx={{
                     fontWeight: 300,
                     mb:         4,
                     fontSize:   { xs: '2.4rem', md: '3.85rem', lg: '4.5rem' },
                     fontFamily: '"Cormorant Garamond", serif',
                     lineHeight: 1.1,
                     fontStyle:  'italic'
                   }}>
                     <Box component="span" sx={{
                       background:           'linear-gradient(135deg, #FFFFFF 0%, #F5F3F0 100%)',
                       backgroundClip:       'text',
                       WebkitBackgroundClip: 'text',
                       color:                'transparent',
                       display:              'block'
                     }}>
                       is Seen
                     </Box>
                   </Typography>
                   <Box sx={{
                     display:        'flex',
                     gap:            2,
                     justifyContent: 'center',
                     alignItems:     'center',
                     flexWrap:       'wrap',
                     mb:             8,
                     flexDirection:  { xs: 'column', sm: 'row' }
                   }}>
                     <Button
                      component={Link}
                      to="/portfolio"
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForward/>}
                      sx={{
                        borderRadius:   '50px',
                        px:             { xs: 4, sm: 5 },
                        py:             1.5,
                        background:     '#A8815B',
                        color:          '#fbfafa',
                        fontSize:       { xs: '1rem', md: '1.1rem' },
                        fontWeight:     500,
                        backdropFilter: 'blur(6px)',
                        border:         '1px solid #675944',
                        '&:hover':      {
                          background: '#785c40',
                          transform:  'translateY(-2px)',
                          boxShadow:  '0 12px 30px rgba(255,255,255,0.08)'
                        },
                        transition:     'all 0.25s ease',
                        width:          { xs: '100%', sm: 'auto' }
                      }}
                      aria-label="View portfolio"
                     >
                       View Portfolio
                     </Button>
                     <Button
                      component={Link}
                      to="/contact"
                      variant="outlined"
                      size="large"
                      sx={{
                        borderRadius:   '50px',
                        px:             { xs: 4, sm: 5 },
                        py:             1.5,
                        borderColor:    'rgba(255,255,255,0.85)',
                        color:          'white',
                        fontSize:       { xs: '1rem', md: '1.1rem' },
                        fontWeight:     500,
                        background:     'rgba(255,255,255,0.06)',
                        backdropFilter: 'blur(6px)',
                        '&:hover':      {
                          background:  'rgba(255,255,255,0.12)',
                          transform:   'translateY(-2px)',
                          borderColor: 'rgba(255,255,255,1)'
                        },
                        transition:     'all 0.25s ease',
                        width:          { xs: '100%', sm: 'auto' }
                      }}
                      aria-label="Begin your journey"
                     >
                       Begin Your Journey
                     </Button>
                   </Box>
                 </Box>
               </Fade>
             </Grid>
           </Grid>
         </Container>

         <Box
          onClick={scrollToCTA}
          sx={{
            position:            'absolute',
            bottom:              {
              xs: `calc(env(safe-area-inset-bottom, 0px) + 24px)`, sm: `calc(env(safe-area-inset-bottom, 0px) + 40px)`
            },
            left:                '50%',
            paddingBottom:       {
              xs: `calc(env(safe-area-inset-bottom, 0px) + 24px)`, sm: `calc(env(safe-area-inset-bottom, 0px) + 40px)`
            },
            transform:           'translateX(-50%) translateY(0)',
            color:               'white',
            opacity:             0.8,
            cursor:              'pointer',
            animation:           'bounce 2s infinite',
            transition:          'transform 0.3s ease, opacity 0.3s ease',
            display:             'flex',
            flexDirection:       'column',
            alignItems:          'center',
            gap:                 1,
            zIndex:              10,
            userSelect:          'none',
            '&:hover':           {
              opacity: 1, transform: 'translateX(-50%) translateY(0) scale(1.06)', animation: 'none'
            },
            '@keyframes bounce': {
              '0%, 20%, 50%, 80%, 100%': { transform: 'translateX(-50%) translateY(0)' },
              '40%':                     { transform: 'translateX(-50%) translateY(-8px)' },
              '60%':                     { transform: 'translateX(-50%) translateY(-4px)' }
            }
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') scrollToCTA() }}
          aria-label="Scroll to content"
         >
           <Typography
            variant="caption"
            sx={{
              fontSize:      '0.75rem',
              letterSpacing: 2,
              textTransform: 'uppercase',
              textShadow:    '0 2px 4px rgba(0,0,0,0.5)',
              color:         'white'
            }}
           >
             Scroll
           </Typography>
           <ArrowForward
            sx={{ transform: 'rotate(90deg)', fontSize: 32, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }}/>
         </Box>
       </Box>
            <Box
                ref={ctaRef}
                sx={{
                minHeight: '60vh',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                background: 'linear-gradient(135deg, #FFFFFF 0%, #FAF9F7 100%)',
                overflow: 'hidden'
            }}>

            <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
            radial-gradient(circle at 30% 50%, rgba(184, 153, 104, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 70% 80%, rgba(184, 153, 104, 0.02) 0%, transparent 50%)
          `,
                    transform: `translateY(${scrollProgress * 0.5}px)`,
                    transition: 'transform 0.1s ease'
                }}/>

                <Container maxWidth="lg" sx={{position: 'relative', zIndex: 2}}>
                    <Fade in={isVisible} timeout={1500}>
                        <Box sx={{textAlign: 'center'}}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontWeight: 300,
                                    color: '#1a1a1a',
                                    mb: 4,
                                    fontSize: {xs: '2.5rem', md: '4rem'},
                                    fontFamily: '"Cormorant Garamond", serif',
                                }}
                            >
                                Begin Your
                                <Box component="span" sx={{
                                    background: 'linear-gradient(135deg, #B89968, #D4AF37)',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    display: 'block',
                                    color: '#1a1a1a',
                                    fontStyle: 'italic'
                                }}>
                                    Transformation
                                </Box>
                            </Typography>

                            <Typography
                                variant="h6"
                                sx={{
                                    color: '#666',
                                    mb: 6,
                                    lineHeight: 1.7,
                                    fontSize: '1.25rem',
                                    fontWeight: 300,
                                    maxWidth: 600,
                                    mx: 'auto',
                                    fontStyle: 'italic'
                                }}
                            >
                                "Let's co-create a space that doesn't just house your life, but elevates it.
                                Where every corner tells your story and every detail sparks joy."
                            </Typography>

                            <Button
                                component={Link}
                                to="/contact"
                                variant="contained"
                                size="large"
                                endIcon={<ArrowForward/>}
                                sx={{
                                    borderRadius: '50px',
                                    px: 5,
                                    py: 1.5,
                                    border: '1px solid rgba(255, 255, 255, 0.4)',
                                    color: '#1a1a1a',
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(25px)',
                                    WebkitBackdropFilter: 'blur(25px)',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                                    textShadow: '0 1px 2px rgba(255, 255, 255, 0.5)',
                                    '&:hover': {
                                        background: 'rgba(255, 255, 255, 0.25)',
                                        backdropFilter: 'blur(30px)',
                                        WebkitBackdropFilter: 'blur(30px)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                                        borderColor: 'rgba(255, 255, 255, 0.6)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Create Your Vision
                            </Button>
                        </Box>
                    </Fade>
                </Container>
            </Box>
        </Box>
    )
}

export default HomePage
